import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Box, ButtonBase, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import AIDescriptionField from "../../AIDescriptionField";
import TextField from "../../TextField";
import NumberInput from "../../NumberInput";
import GalleryImage from "../../GalleryImage";
import Service from "../../../api/Service";
import ServiceGallery from "../../../api/ServiceGallery";
import { useCookies } from "react-cookie";
import { useLoaderData, useNavigate } from "react-router-dom";
import BottomButton from "../../BottomButton";
import { getPreferredLanguage } from "../../../utils/languageUtils";
import { CustomError } from "../../../types";
import DeleteDialog from "../../Modals/DeleteDialog";
import ErrorDialog from "../../ErrorDialog";
import {
    getCloudinaryOriginalUrl,
    uploadToCloudinary,
} from "../../../utils/cloudinaryUtils";
import Brand from "../../../api/Brand";
import ShareableLink from "../../ShareableLink";
import ImageCropperModal from "../../ImageCropperModal";
import { CARD_STYLES, FONTS, FORM_LABEL_SX } from "../../../constants/styles";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import AccessTimeRounded from "@mui/icons-material/AccessTimeRounded";
import AttachMoneyRounded from "@mui/icons-material/AttachMoneyRounded";
import AddRounded from "@mui/icons-material/AddRounded";
import DeleteOutlineRounded from "@mui/icons-material/DeleteOutlineRounded";
import CameraAltRounded from "@mui/icons-material/CameraAltRounded";
import { useFilePicker } from "use-file-picker";
import {
    FileAmountLimitValidator,
    FileTypeValidator,
    FileSizeValidator,
} from "use-file-picker/validators";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

export const loader = async (request: any) => {
    const category_id = await request.params.category_id;
    const service_id = await request.params.service_id;
    return { category_id, service_id };
};

const ServiceEdit: React.FC = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const category_id = (useLoaderData() as any).category_id as string;
    const service_id = (useLoaderData() as any).service_id as string;

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [service, setService] = useState<Service | undefined>();

    const [name, setName] = useState("");
    const [duration, setDuration] = useState(0);
    const [price, setPrice] = useState(0);
    const [image, setImage] = useState<string | null | undefined>(); // actual base64 string for upload
    const [imagePreview, setImagePreview] = useState<string | undefined>(); // either base64 or url to display
    const [description, setDescription] = useState("");
    const [serviceShareableUrl, setServiceShareableUrl] = useState<string>();
    const [reschedule, setReschedule] = useState(false);
    const [gallery, setGallery] = useState(false);
    const [serviceGallery, setServiceGallery] = useState<ServiceGallery[]>([]);
    const [descriptionError, setDescriptionError] = useState<string | null>(
        null
    );
    const [isSaving, setIsSaving] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [uploading, setUploading] = useState(false); // Track upload state

    // crop modal state
    const [cropOpenMain, setCropOpenMain] = useState(false);
    const [cropSourceMain, setCropSourceMain] = useState<string | null>(null);
    const [cropOpenGallery, setCropOpenGallery] = useState(false);
    const [cropSourceGallery, setCropSourceGallery] = useState<string | null>(
        null
    );
    const [editingKey, setEditingKey] = useState<string | null>(null);

    const durationInputRef = useRef<HTMLInputElement>(null);
    const priceInputRef = useRef<HTMLInputElement>(null);
    const navigate = useNavigate();

    // Scroll tracking for compact sticky header
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    // derive original for recropping (strip transforms)
    const originalForCrop = useMemo(() => {
        return imagePreview ? getCloudinaryOriginalUrl(imagePreview) : null;
    }, [imagePreview]);

    // File picker for main service image
    const { openFilePicker: openMainImagePicker } = useFilePicker({
        readAs: "DataURL",
        accept: "image/*",
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(["jpeg", "jpg", "png", "heic", "heif"]),
            new FileSizeValidator({ maxFileSize: 10 * 1024 * 1024 }),
        ],
        onFilesSelected: ({ errors }) => {
            if (errors?.length) console.error("File upload error:", errors);
        },
        onFilesSuccessfullySelected: ({ filesContent }) => {
            if (filesContent[0]) onServiceImageUpload(filesContent[0].content);
        },
    });

    // File picker for gallery images
    const { openFilePicker: openGalleryPicker } = useFilePicker({
        readAs: "DataURL",
        accept: "image/*",
        multiple: false,
        validators: [
            new FileAmountLimitValidator({ max: 1 }),
            new FileTypeValidator(["jpeg", "jpg", "png", "heic", "heif"]),
            new FileSizeValidator({ maxFileSize: 10 * 1024 * 1024 }),
        ],
        onFilesSelected: ({ errors }) => {
            if (errors?.length) console.error("File upload error:", errors);
        },
        onFilesSuccessfullySelected: ({ filesContent }) => {
            if (filesContent[0]) onServiceGalleryAdd(filesContent[0].content);
        },
    });

    useEffect(() => {
        const fetchService = async () => {
            if (!uuid || !brand_id) {
                navigate("/");
                return;
            }
            const service = await Service.info(
                uuid,
                brand_id,
                service_id,
                category_id
            );
            if (!service) return;
            setService(service);
            setName(service.name);
            setDuration(service.duration);
            setPrice(service.price);
            setImagePreview(
                service.image ||
                    sessionStorage.getItem(`${service.key}_image`) ||
                    undefined
            );
            setDescription(service.description);
            setReschedule(service.reschedule);
            setGallery(service.gallery);
            if (service.clientKey) {
                const brand = await Brand.info(uuid, brand_id);
                if (!brand) return;
                setServiceShareableUrl(
                    `https://${brand.address}.jemloc.com/services/${service.clientKey}`
                );
            }
        };
        fetchService();
    }, [uuid, brand_id, service_id, category_id, navigate]);

    useEffect(() => {
        if (imagePreview) {
            sessionStorage.setItem(`${service!.key}_image`, imagePreview);
        }
    }, [service, imagePreview]);

    const handleMainCropSave = (finalUrl: string) => {
        setImage(finalUrl); // value sent to BE on "Save Changes"
        setImagePreview(finalUrl); // immediate UI
        setCropOpenMain(false);
        setCropSourceMain(null);
    };

    const handleMainCropCancel = () => {
        setCropOpenMain(false);
        setCropSourceMain(null);
    };

    const openGalleryCrop = (imgUrl: string, key: string) => {
        setEditingKey(key);
        setCropSourceGallery(getCloudinaryOriginalUrl(imgUrl));
        setCropOpenGallery(true);
    };

    const handleGalleryCropSave = async (finalUrl: string) => {
        if (!uuid || !brand_id) return;

        if (editingKey) {
            await ServiceGallery.update(
                uuid,
                brand_id,
                category_id,
                service_id,
                editingKey,
                finalUrl
            );
        } else {
            await ServiceGallery.register(
                uuid,
                brand_id,
                category_id,
                service_id,
                finalUrl
            );
        }

        setCropOpenGallery(false);
        setCropSourceGallery(null);
        setEditingKey(null);
        loadGallery();
    };

    const onDeleteService = useCallback(async () => {
        if (!uuid || !brand_id || !service) return;
        setIsDeleting(true);

        try {
            const success = await Service.delete(
                uuid,
                brand_id,
                category_id,
                service_id
            );
            if (success) navigate("/menu/services");
            else {
                setDeleteDialogOpen(false);
                setErrorMessage(
                    "Failed to delete the service. Please try again."
                );
                setErrorDialogOpen(true);
            }
        } catch (err) {
            console.error("Failed to delete service: ", err);
            setDeleteDialogOpen(false);
            setErrorMessage(
                "An unexpected error occurred while trying to delete the service."
            );
            setErrorDialogOpen(true);
        } finally {
            setIsDeleting(false);
        }
    }, [
        uuid,
        brand_id,
        service,
        category_id,
        service_id,
        navigate,
    ]);

    const openDeleteDialog = () => setDeleteDialogOpen(true);
    const closeDeleteDialog = () => {
        if (!isDeleting) setDeleteDialogOpen(false);
    };

    const onExitOrSave = useCallback(async () => {
        if (!uuid || !brand_id || !service || isSaving) return;
        setDescriptionError(null);
        setIsSaving(true);
        try {
            if (uploading) {
                alert("Waiting for image upload to complete...");
                while (uploading)
                    await new Promise((res) => setTimeout(res, 200)); // Poll until upload completes
            }
            await Service.update(
                uuid,
                brand_id,
                category_id,
                service_id,
                name,
                duration,
                price,
                gallery,
                description,
                reschedule,
                service.enabled,
                service.position,
                getPreferredLanguage(),
                image
            );
            navigate("/menu/services");
        } catch (err) {
            setDescriptionError(
                (err as CustomError).response?.data?.error ||
                    "An error occurred while updating the business description."
            );
            setIsSaving(false);
        }
    }, [
        uuid,
        brand_id,
        category_id,
        service_id,
        name,
        duration,
        price,
        image,
        description,
        reschedule,
        gallery,
        service,
        navigate,
        uploading,
        isSaving,
    ]);

    const loadGallery = useCallback(() => {
        if (!uuid || !brand_id) return;
        ServiceGallery.list(uuid, brand_id, category_id, service_id)
            .then((res) => {
                setServiceGallery(res ?? []);
            })
            .catch(console.error);
    }, [uuid, brand_id, category_id, service_id]);

    useEffect(loadGallery, [loadGallery]);

    const onServiceGalleryRemove = useCallback(
        (key: string) => {
            if (!uuid || !brand_id) return;
            ServiceGallery.unregister(
                uuid,
                brand_id,
                category_id,
                service_id,
                key
            ).then(() => {
                setServiceGallery((items) =>
                    items.filter((x) => x.key !== key)
                );
            });
        },
        [uuid, brand_id, category_id, service_id]
    );

    const onServiceImageRemove = () => {
        setImage(null);
        setImagePreview(undefined);
        sessionStorage.removeItem(`${service!.key}_image`);
    };

    const onServiceImageUpload = async (base64Image?: string) => {
        if (!base64Image) return;
        setUploading(true);

        try {
            const stagingUrl = await uploadToCloudinary(base64Image); // Upload to staging
            if (stagingUrl) {
                setCropSourceMain(stagingUrl);
                setCropOpenMain(true);
            } else {
                console.error("Failed to upload service image to Cloudinary.");
            }
        } catch (error) {
            console.error("Error during service image upload:", error);
        } finally {
            setUploading(false);
        }
    };

    const onServiceGalleryAdd = async (base64Image?: string) => {
        if (!uuid || !brand_id || !base64Image) return;
        setUploading(true);

        try {
            const stagingUrl = await uploadToCloudinary(base64Image); // Upload to staging
            if (stagingUrl) {
                setEditingKey(null);
                setCropSourceGallery(stagingUrl);
                setCropOpenGallery(true);
            } else {
                console.error("Failed to upload gallery image to Cloudinary.");
            }
        } catch (error) {
            console.error("Error during gallery image upload:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <>
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Image Hero ── */}
                <ButtonBase
                    onClick={() => {
                        if (imagePreview) {
                            setCropSourceMain(originalForCrop ?? imagePreview);
                            setCropOpenMain(true);
                        } else {
                            openMainImagePicker();
                        }
                    }}
                    sx={{ width: "100%", display: "block", textAlign: "left" }}
                >
                    <Stack
                        ref={heroRef}
                        sx={{
                            width: "100%",
                            minHeight: imagePreview ? 200 : 160,
                            position: "relative",
                            zIndex: 0,
                            background: imagePreview
                                ? `linear-gradient(to bottom, rgba(0,0,0,0) 40%, rgba(0,0,0,0.4) 100%), url(${imagePreview})`
                                : `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            transition: "background 0.3s ease",
                            pt: "48px",
                            pb: 3,
                            px: 2,
                            ...(!imagePreview && {
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    inset: 0,
                                    opacity: 0.04,
                                    backgroundImage: NOISE_TEXTURE,
                                    backgroundRepeat: "repeat",
                                    backgroundSize: "128px 128px",
                                    pointerEvents: "none",
                                },
                            }),
                        }}
                    >
                        {/* Back arrow */}
                        <ButtonBase
                            onClick={(e) => {
                                e.stopPropagation();
                                onExitOrSave();
                            }}
                            sx={{
                                position: "absolute",
                                top: 12,
                                left: 10,
                                zIndex: 2,
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                bgcolor: "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            {isSaving ? (
                                <CircularProgress size={18} sx={{ color: "white" }} />
                            ) : (
                                <ColoredIcon icon={BackIcon} color="white" size="28px" />
                            )}
                        </ButtonBase>

                        {/* Center hint */}
                        <Stack flex={1} alignItems="center" justifyContent="center">
                            {imagePreview ? (
                                <Box
                                    sx={{
                                        bgcolor: "rgba(60,60,60,0.35)",
                                        backdropFilter: "blur(16px)",
                                        WebkitBackdropFilter: "blur(16px)",
                                        border: "1px solid rgba(255,255,255,0.2)",
                                        borderRadius: "16px",
                                        px: 1.5,
                                        py: 0.5,
                                    }}
                                >
                                    <Typography
                                        fontSize="12px"
                                        fontWeight={500}
                                        color="white"
                                    >
                                        Tap to adjust photo
                                    </Typography>
                                </Box>
                            ) : (
                                <Stack alignItems="center" spacing={1}>
                                    <CameraAltRounded
                                        sx={{ fontSize: 32, color: "white", opacity: 0.7 }}
                                    />
                                    <Typography
                                        fontSize="13px"
                                        fontWeight={500}
                                        color="rgba(255,255,255,0.7)"
                                    >
                                        Add service photo
                                    </Typography>
                                </Stack>
                            )}
                        </Stack>
                    </Stack>
                </ButtonBase>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition:
                                "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                        }}
                    >
                        <ButtonBase
                            onClick={onExitOrSave}
                            disabled={isSaving}
                            sx={{ p: 0.5 }}
                        >
                            {isSaving ? (
                                <CircularProgress size={18} sx={{ color: primary }} />
                            ) : (
                                <ColoredIcon
                                    icon={BackIcon}
                                    color={primary}
                                    size="28px"
                                />
                            )}
                        </ButtonBase>
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                            ml={0.5}
                            noWrap
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            {name || "Edit Service"}
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Bottom Sheet Content ── */}
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        boxSizing: "border-box",
                        px: 2,
                        pt: 0.5,
                        pb: "calc(4vh + 90px)",
                        animation: "fadeSlideIn 0.3s ease both",
                    }}
                    spacing={2}
                >
                    {/* Service Name */}
                    <TextField
                        value={name}
                        onChange={(e) => setName(e.currentTarget.value)}
                        variant="underlined"
                        nextRef={durationInputRef}
                        fullWidth
                        sx={{
                            "& input": {
                                fontFamily: FONTS.display,
                                fontSize: "20px",
                                fontWeight: 600,
                            },
                        }}
                    />

                    {/* Duration + Price — side-by-side cards */}
                    <Stack direction="row" spacing={1.5}>
                        {/* Duration */}
                        <Stack flex={1} sx={{ ...CARD_STYLES, p: 1.5 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                mb={1}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "10px",
                                        bgcolor: alpha(primary, 0.1),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AccessTimeRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Duration
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="baseline"
                                spacing={0.5}
                            >
                                <NumberInput
                                    value={duration}
                                    onChange={setDuration}
                                    minValue={15}
                                    maxLength={3}
                                    width={60}
                                    ref={durationInputRef}
                                    nextRef={priceInputRef}
                                />
                                <Typography
                                    fontSize="13px"
                                    color="text.secondary"
                                >
                                    min
                                </Typography>
                            </Stack>
                        </Stack>

                        {/* Price */}
                        <Stack flex={1} sx={{ ...CARD_STYLES, p: 1.5 }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                                mb={1}
                            >
                                <Box
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        borderRadius: "10px",
                                        bgcolor: alpha(primary, 0.1),
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                    }}
                                >
                                    <AttachMoneyRounded
                                        sx={{ fontSize: 18, color: primary }}
                                    />
                                </Box>
                                <Typography sx={FORM_LABEL_SX}>
                                    Price
                                </Typography>
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="baseline"
                                spacing={0.5}
                            >
                                <NumberInput
                                    value={price}
                                    onChange={setPrice}
                                    minValue={1}
                                    maxLength={6}
                                    width={80}
                                    supportDecimal
                                    ref={priceInputRef}
                                    onKeyDown={(
                                        e: React.KeyboardEvent<HTMLInputElement>
                                    ) => {
                                        if (e.key === "Enter") {
                                            e.currentTarget.blur();
                                        }
                                    }}
                                />
                                <Typography
                                    fontSize="13px"
                                    color="text.secondary"
                                >
                                    {service?.currency || ""}
                                </Typography>
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Description */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Description
                    </Typography>
                    <Stack sx={CARD_STYLES}>
                        <AIDescriptionField
                            placeholder="Type a service description"
                            value={description}
                            setText={setDescription}
                            contextType="service_description"
                            uuid={uuid}
                            brandKey={brand_id}
                            serviceId={service?.key}
                            width="100%"
                        />
                        {descriptionError && (
                            <Typography
                                color="error.main"
                                fontSize="13px"
                                mt={1}
                            >
                                {descriptionError}
                            </Typography>
                        )}
                    </Stack>

                    {/* Gallery */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Gallery
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1.5}
                        sx={{
                            overflowX: "auto",
                            scrollbarWidth: "none",
                            "&::-webkit-scrollbar": { display: "none" },
                            mx: -2,
                            px: 2,
                            pb: 0.5,
                        }}
                    >
                        {serviceGallery.map((x) => (
                            <ButtonBase
                                key={x.key}
                                onClick={() => openGalleryCrop(x.image!, x.key)}
                                sx={{ borderRadius: "16px", flexShrink: 0 }}
                            >
                                <GalleryImage
                                    image={x.image!}
                                    width="120px"
                                    height="120px"
                                    borderRadius="16px"
                                    expandable={false}
                                />
                            </ButtonBase>
                        ))}
                        {/* Add photo button */}
                        <ButtonBase
                            onClick={openGalleryPicker}
                            sx={{
                                width: 120,
                                height: 120,
                                flexShrink: 0,
                                borderRadius: "16px",
                                border: "2px dashed",
                                borderColor: alpha(primary, 0.2),
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 0.5,
                            }}
                        >
                            <AddRounded
                                sx={{
                                    fontSize: 24,
                                    color: alpha(primary, 0.4),
                                }}
                            />
                            <Typography fontSize="11px" color="text.secondary">
                                Add photo
                            </Typography>
                        </ButtonBase>
                    </Stack>

                    {/* Shareable Link */}
                    {serviceShareableUrl && (
                        <ShareableLink
                            url={serviceShareableUrl}
                            label="Direct link to booking"
                            showUrl={false}
                        />
                    )}

                    {/* Delete — danger zone at bottom */}
                    <ButtonBase
                        onClick={openDeleteDialog}
                        sx={{
                            py: 1.5,
                            px: 1,
                            borderRadius: "12px",
                            alignSelf: "center",
                            mt: 2,
                        }}
                    >
                        <DeleteOutlineRounded
                            sx={{
                                fontSize: 18,
                                color: "text.secondary",
                                mr: 0.75,
                            }}
                        />
                        <Typography
                            fontSize="13px"
                            fontWeight={500}
                            color="text.secondary"
                        >
                            Delete Service
                        </Typography>
                    </ButtonBase>

                    <DeleteDialog
                        open={deleteDialogOpen}
                        isDeleting={isDeleting}
                        entityName="service"
                        onClose={closeDeleteDialog}
                        onDelete={onDeleteService}
                    />
                    <ErrorDialog
                        open={errorDialogOpen}
                        errorMessage={errorMessage}
                        onClose={() => setErrorDialogOpen(false)}
                    />
                </Stack>
            </Stack>

            <BottomButton onClick={onExitOrSave} disabled={uploading || isSaving} sticky>
                Save Changes
            </BottomButton>
            <ImageCropperModal
                open={cropOpenMain}
                src={cropSourceMain}
                onCancel={handleMainCropCancel}
                onSave={handleMainCropSave}
                onDelete={onServiceImageRemove}
                mode="square"
                outSize={{ w: 430, h: 430 }}
                title="Adjust service image"
            />
            <ImageCropperModal
                open={cropOpenGallery}
                src={cropSourceGallery}
                onCancel={() => {
                    setCropOpenGallery(false);
                    setEditingKey(null);
                    setCropSourceGallery(null);
                }}
                onSave={handleGalleryCropSave}
                onDelete={
                    editingKey
                        ? async () => {
                              await onServiceGalleryRemove(editingKey);
                              setCropOpenGallery(false);
                              setEditingKey(null);
                              setCropSourceGallery(null);
                          }
                        : undefined
                }
                mode="square"
                outSize={{ w: 430, h: 430 }}
                title="Adjust gallery image"
            />
        </>
    );
};

export default ServiceEdit;
